'use client';

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const toggleGroupVariants = cva('inline-flex items-center justify-center', {
    variants: {
        variant: {
            default: 'bg-background hover:bg-accent/50',
            cyber: 'bg-card/50 hover:bg-accent/50',
        },
        size: {
            default: 'h-8 px-3',
            sm: 'h-7 px-2 text-xs',
            lg: 'h-9 px-4',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});

const ToggleGroupContext = React.createContext<
    VariantProps<typeof toggleGroupVariants>
>({
    size: 'default',
    variant: 'default',
});

function ToggleGroup({
    className,
    variant,
    size,
    children,
    ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleGroupVariants>) {
    return (
        <ToggleGroupPrimitive.Root
            data-slot="toggle-group"
            className={cn(
                'flex items-center gap-0 overflow-hidden rounded-lg border border-border',
                className,
            )}
            {...props}
        >
            <ToggleGroupContext.Provider value={{ variant, size }}>
                {children}
            </ToggleGroupContext.Provider>
        </ToggleGroupPrimitive.Root>
    );
}

function ToggleGroupItem({
    className,
    children,
    variant,
    size,
    ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleGroupVariants>) {
    const context = React.useContext(ToggleGroupContext);

    return (
        <ToggleGroupPrimitive.Item
            data-slot="toggle-group-item"
            className={cn(
                toggleGroupVariants({
                    variant: variant ?? context.variant,
                    size: size ?? context.size,
                }),
                // Base styles
                'font-mono text-xs font-semibold text-muted-foreground cursor-pointer',
                'transition-all duration-150 outline-none',
                'focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1',
                'disabled:pointer-events-none disabled:opacity-50',
                // Selected state — neutral fill, but the inset ring must hold
                // ≥3:1 against the off-state background (WCAG 1.4.11).
                'data-[state=on]:bg-[#23261d] data-[state=on]:text-foreground',
                'data-[state=on]:inset-ring data-[state=on]:inset-ring-[rgba(236,238,228,0.45)]',
                // Border separator between items
                'border-l border-l-border/50 first:border-l-0',
                className,
            )}
            {...props}
        >
            {children}
        </ToggleGroupPrimitive.Item>
    );
}

export { ToggleGroup, ToggleGroupItem, toggleGroupVariants };
